<script lang="ts">
  import { i18n } from '../i18n/index.svelte';
  import PixelAvatar from './PixelAvatar.svelte';
  import PixelButton from './PixelButton.svelte';
  import PixelLoader from './PixelLoader.svelte';
  import SegmentedBar from './SegmentedBar.svelte';

  const t = $derived(i18n.t);

  interface Props {
    name: string;
    avatarId: number;
    /** Null while unknown or never rated — never substituted with a made-up number. */
    rating: number | null;
    /** This player's own wait. */
    elapsedMs: number;
    /** How far the *room's* authoritative deadline has run. */
    deadlineElapsedMs: number;
    timeoutMs: number;
    oncancel: () => void;
  }

  let { name, avatarId, rating, elapsedMs, deadlineElapsedMs, timeoutMs, oncancel }: Props =
    $props();

  const clock = $derived(formatElapsed(elapsedMs));

  function formatElapsed(ms: number): string {
    const seconds = Math.max(0, Math.floor(ms / 1000));
    return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }
</script>

<!--
  A dedicated screen rather than the battle HUD with an overlay on it. Waiting
  in a queue is not a match in progress: showing empty score panels and an idle
  board tells the player the game is about to be interrupted by a result, when
  what they actually need to know is how long they have been waiting and how to
  get out.
-->
<div class="mm">
  <div class="mm__versus">
    <div class="mm__side">
      <div class="mm__avatar mm__avatar--self">
        <PixelAvatar {avatarId} />
      </div>
      <span class="mm__name">{name}</span>
    </div>

    <span class="mm__vs" aria-hidden="true">{t.matchmaking.versus}</span>

    <div class="mm__side">
      <div class="mm__avatar mm__avatar--empty">
        <PixelAvatar avatarId={0} state="empty" />
      </div>
      <span class="mm__name mm__name--empty">- - - -</span>
    </div>
  </div>

  <PixelLoader tone="mag" label={t.matchmaking.ariaSearching} />

  <strong class="mm__status">{t.matchmaking.searching}<br />{t.matchmaking.opponent}</strong>

  <div class="panel mm__panel">
    <div class="mm__row">
      <span class="lbl">{t.matchmaking.time}</span>
      <span class="mm__time">{clock}</span>
    </div>

    <!--
      The player's own rating, deliberately not framed as a matching window: the
      public queue does not currently narrow by rating, and a "1520 - 1730" band
      would be describing filtering that does not happen.
    -->
    <div class="mm__row">
      <span class="lbl">{t.matchmaking.yourElo}</span>
      <span class="mm__elo">{rating ?? t.matchmaking.unranked}</span>
    </div>

    <!-- The bar tracks the room's deadline, which is what actually ends the
         search; the counter above tracks this player's wait. They are different
         clocks and would be a lie if drawn from one value. -->
    <SegmentedBar value={deadlineElapsedMs} max={timeoutMs} segments={8} tone="danger" />
    <span class="tiny mm__hint">{t.matchmaking.autoCancel}</span>
  </div>

  <PixelButton variant="danger" onclick={oncancel}>{t.common.cancel}</PixelButton>
</div>
